/**
 * LLM Usage — operator view of the telemetry ledger (ADR-183 / ADR-188).
 *
 * The page deliberately renders token buckets rather than prices. Pricing is
 * a separate concern from the retained usage facts and belongs to the vendor
 * pull described in the telemetry plan.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useSearchParams } from 'react-router-dom'
import { api } from '../api/client'
import type {
  LlmUsageQueryParams,
  LlmUsageRollupResponse,
  LlmUsageRollupRow,
  LlmUsageRoot,
  LlmUsageTraceResponse,
  LlmUsageTracesResponse,
} from '../api/client'
import { SpanTree } from '../components/SpanTree'
import { StatusBadge, cronDisplayName, cronIdentity } from '../components/llmUsageAttribution'
import { TelemetryBanner } from '../components/TelemetryBanner'
import { formatMs } from '../utils/formatMs'

const GROUPS = [
  'provider',
  'profile',
  'activity',
  'step',
  'cron',
  'run',
  'model',
  'feature',
] as const

const WINDOWS = [
  { value: '1h', label: 'Last hour' },
  { value: '24h', label: 'Last 24 hours' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
]

const FILTER_DEFAULTS = {
  window: '24h',
  group_by: 'provider',
  skill: '',
  provider: '',
} as const

function formatCount(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—'
  return value.toLocaleString('en-US')
}

function formatDays(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—'
  return `${value} days`
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toISOString().slice(0, 16).replace('T', ' ')
}

function groupKeyHref(groupBy: string, key: string, searchParams: URLSearchParams): string {
  const params = new URLSearchParams()
  const window = searchParams.get('window')
  if (window) params.set('window', window)
  params.set('group_by', groupBy)
  params.set('key', key)
  return `/llm-usage/traces?${params.toString()}`
}

function rowLabel(row: LlmUsageRollupRow): string {
  return row.label ?? row.key ?? 'unattributed'
}

function traceHref(groupBy: string, row: LlmUsageRollupRow, filters?: Pick<LlmUsageQueryParams, 'window' | 'skill' | 'provider'>): string {
  const params = new URLSearchParams({ group_by: groupBy })
  if (row.key) params.set('key', row.key)
  for (const key of ['window', 'skill', 'provider'] as const) {
    const value = filters?.[key]
    if (value) params.set(key, value)
  }
  return `/llm-usage/traces?${params.toString()}`
}

const TRACE_FILTER_KEYS = ['window', 'group_by', 'skill', 'provider', 'key'] as const

function traceFilters(searchParams: URLSearchParams): URLSearchParams {
  const filters = new URLSearchParams()
  for (const key of TRACE_FILTER_KEYS) {
    const value = searchParams.get(key)
    if (value) filters.set(key, value)
  }
  return filters
}

function queryParamsFrom(searchParams: URLSearchParams): LlmUsageQueryParams {
  const params: LlmUsageQueryParams = {}
  for (const key of ['window', 'group_by', 'skill', 'provider', 'key', 'trace_id', 'cursor'] as const) {
    const value = searchParams.get(key)
    if (value) params[key] = value
  }
  const limit = searchParams.get('limit')
  if (limit) {
    const parsed = Number(limit)
    if (Number.isFinite(parsed)) params.limit = parsed
  }
  return params
}

function traceRootHref(root: LlmUsageRoot, searchParams: URLSearchParams): string {
  const params = traceFilters(searchParams)
  params.set('trace_id', root.trace_id)
  return `/llm-usage/trace?${params.toString()}`
}

function TraceHealth({ health }: { health?: LlmUsageTracesResponse['health'] | LlmUsageTraceResponse['health'] }): React.ReactNode {
  if (!health) return null
  // Rejected events were refused at ingest and are gone; a page that reports
  // only drops says nothing about the day this fleet lost every agent event.
  const lost = (health.rejected_24h ?? 0) > 0 || (health.dropped_24h ?? 0) > 0
  // The severity must be in the words, not only in the colour: a screen
  // reader announcing this card should hear that events were lost.
  const capture = `${formatCount(health.accepted_24h)} accepted`
    + ` · ${formatCount(health.rejected_24h)} rejected`
    + ` · ${formatCount(health.dropped_24h)} dropped`
    + (lost ? ' · events lost' : '')
  return (
    <>
      {health.migration_error && (
        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          Telemetry migration failed: {health.migration_error}
        </div>
      )}
      <section aria-label="LLM Usage health" className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <HealthCard label="Target coverage" value={formatDays(health.target_coverage_days)} />
        <HealthCard label="Estimated coverage" value={formatDays(health.estimated_coverage_days)} />
        <HealthCard label="Oldest retained span" value={formatDate(health.oldest_started_at)} />
        {/* Deliberately scoped to 24h: whether the retained history is complete
            needs the per-day capture gaps and the retention floor, not this. */}
        <HealthCard label="Capture events · last 24h" value={capture} alert={lost} />
        <HealthCard label="Ingest lag" value={formatMs(health.ingest_lag_ms, 'unknown')} />
      </section>
    </>
  )
}

function TraceListRow({ root, searchParams }: { root: LlmUsageRoot; searchParams: URLSearchParams }): React.ReactNode {
  // A run root carries no name, so without the cron or the activity behind it
  // every row on this list reads as its profile.
  const cron = cronIdentity(root)
  const label = root.name ?? cronDisplayName(root) ?? root.activity ?? root.profile ?? root.trace_id
  // The key filter is evaluated over a trace's usage rows, so a run that made
  // no model call matches no key and its own drill-down would come back empty.
  const cronName = cronDisplayName(root)
  // Gated on the cron identity, never on its label: a cron with an empty label
  // still has a key to filter by, and losing the link over a display string
  // would be the weaker of the two tests.
  const drillDown = root.calls === 0
    ? null
    : cron
      ? groupKeyHref('cron', cron, searchParams)
      : root.activity
        ? groupKeyHref('activity', root.activity, searchParams)
        : null
  const filtersBy = drillDown === null ? null : cron ? (cronName || cron) : root.activity
  // A link has to name what it filters by. When the title already said that
  // name, repeating it says nothing, so the link takes wording that names the
  // filter instead — never the other half of the row, which would send an
  // operator somewhere its own text did not mention.
  const drillDownText = drillDown === null
    ? null
    : filtersBy !== label
      ? filtersBy
      : cron ? 'other runs of this cron' : 'other runs of this activity'
  // Whatever the title and the link did not already say. A cron named after the
  // one activity it runs makes these the same string; say it once.
  const aside = [root.activity, cronName]
    .filter((value): value is string => Boolean(value) && value !== label && value !== drillDownText)
    .filter((value, index, values) => values.indexOf(value) === index)
    .join(' · ')

  return (
    <li className="border-t border-gray-100 px-4 py-3 first:border-t-0">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
        <Link to={traceRootHref(root, searchParams)} className="min-w-40 flex-1 font-medium text-indigo-700 hover:text-indigo-900">
          {label}
        </Link>
        {root.profile && <span className="text-gray-600">{root.profile}</span>}
        {root.trigger && <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600">{root.trigger}</span>}
        <StatusBadge status={root.status} />
        <span className="text-xs text-gray-500">{formatCount(root.calls)} calls</span>
        <span className="text-xs text-gray-400">{formatDate(root.started_at)}</span>
      </div>
      {(aside || drillDown) && (
        <div className="mt-1 flex flex-wrap gap-x-2 text-xs text-gray-500">
          {aside && <span className="font-mono">{aside}</span>}
          {drillDown && (
            <Link to={drillDown} className="font-mono text-indigo-600 hover:text-indigo-800">{drillDownText}</Link>
          )}
        </div>
      )}
    </li>
  )
}

function usePagedLlmUsage<T extends { next_cursor: string | null }>(
  fetchPage: ((cursor?: string) => Promise<T>) | null,
  merge: (previous: T, next: T) => T,
  failureMessage: string,
) {
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(fetchPage !== null)
  const [loadingMore, setLoadingMore] = useState(false)

  const load = useCallback(async (cursor?: string, append = false) => {
    if (!fetchPage) {
      setLoading(false)
      return
    }
    if (append) setLoadingMore(true)
    else setLoading(true)
    setError(null)
    try {
      const response = await fetchPage(cursor)
      setData(previous => append && previous ? merge(previous, response) : response)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : failureMessage)
    } finally {
      if (append) setLoadingMore(false)
      else setLoading(false)
    }
  }, [failureMessage, fetchPage, merge])

  useEffect(() => { void load() }, [load])
  const loadMore = data?.next_cursor ? () => load(data.next_cursor ?? undefined, true) : undefined
  return { data, error, loading, loadingMore, load, loadMore }
}

function KeyFilterChip({ activeKey, onClear }: { activeKey: string; onClear: () => void }): React.ReactNode {
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <span className="text-gray-500">Filtered to</span>
      <button
        type="button"
        onClick={onClear}
        aria-label={`Clear the ${activeKey} filter`}
        className="flex items-center gap-1.5 rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 font-mono text-xs text-indigo-700 hover:bg-indigo-100"
      >
        {activeKey}
        <span aria-hidden="true">×</span>
      </button>
    </div>
  )
}

function LlmUsageTraces() {
  const [searchParams, setSearchParams] = useSearchParams()
  const params = useMemo(() => queryParamsFrom(searchParams), [searchParams])
  const fetchPage = useCallback((cursor?: string) => api.getLlmUsageTraces({ ...params, cursor }), [params])
  // Pages can overlap when a root is written between requests, so a root is
  // carried once by its span id rather than appended twice.
  const merge = useCallback((previous: LlmUsageTracesResponse, next: LlmUsageTracesResponse) => {
    const byId = new Map(previous.roots.map(root => [root.span_id, root]))
    for (const root of next.roots) byId.set(root.span_id, root)
    return { ...next, roots: Array.from(byId.values()) }
  }, [])
  const { data, error, loading, loadingMore, loadMore } = usePagedLlmUsage(fetchPage, merge, 'Unable to load traces.')

  const roots = [...(data?.roots ?? [])].sort((a, b) => Date.parse(b.started_at) - Date.parse(a.started_at))
  // Before traceFilters(searchParams): the compiler has to assume a call may mutate
  // its argument, and a callback memoized on a value mutated above it cannot be kept.
  const clearKey = useCallback(() => {
    const next = new URLSearchParams(searchParams)
    next.delete('key')
    setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams])
  const filters = traceFilters(searchParams)
  const activeKey = searchParams.get('key')

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">LLM Usage traces</h1>
          <p className="mt-1 text-sm text-gray-500">Recent run, turn and cron roots.</p>
        </div>
        <Link to={`/llm-usage${filters.toString() ? `?${filters.toString()}` : ''}`} className="text-sm font-medium text-indigo-700 hover:text-indigo-900">
          ← Back to rollup
        </Link>
      </div>
      <TraceHealth health={data?.health} />
      {activeKey && <KeyFilterChip activeKey={activeKey} onClear={clearKey} />}
      {error && <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
      {data?.telemetry === 'unavailable' && <TelemetryBanner />}
      {loading && <p className="text-sm text-gray-500">Loading traces…</p>}
      {!loading && data && data.telemetry !== 'unavailable' && (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          {roots.length === 0
            ? <p className="px-5 py-10 text-center text-sm text-gray-400">{data.next_cursor
              ? 'No matches yet in the traces searched so far.'
              : 'No traces in the selected window.'}</p>
            : <ul>{roots.map(root => <TraceListRow key={root.span_id} root={root} searchParams={searchParams} />)}</ul>}
          {data.next_cursor && (
            <div className="border-t border-gray-100 px-4 py-3">
              <button
                type="button"
                onClick={loadMore}
                disabled={loadingMore}
                className="rounded border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-sm font-medium text-indigo-700 disabled:opacity-50"
              >
                {loadingMore ? 'Loading…' : roots.length === 0 ? 'Search further' : 'Load more'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function LlmUsageTrace() {
  const [searchParams] = useSearchParams()
  const traceId = searchParams.get('trace_id') ?? ''
  const [missingTrace, setMissingTrace] = useState(!traceId)
  useEffect(() => { setMissingTrace(!traceId) }, [traceId])
  const fetchPage = useCallback((cursor?: string) => api.getLlmUsageTrace({ trace_id: traceId, limit: 2000, cursor }), [traceId])
  const merge = useCallback((previous: LlmUsageTraceResponse, next: LlmUsageTraceResponse) => ({ ...next, spans: [...previous.spans, ...next.spans] }), [])
  const paged = usePagedLlmUsage(traceId ? fetchPage : null, merge, 'Unable to load trace.')
  const { data, error, loading, loadingMore, loadMore } = paged
  const effectiveError = missingTrace ? 'No trace_id was supplied.' : error

  const filters = traceFilters(searchParams)
  const backHref = `/llm-usage/traces${filters.toString() ? `?${filters.toString()}` : ''}`
  // The trace's own span count, not the page's: this card sits beside a
  // trace-wide token total, and two denominators in one row misread as one.
  // Against a fleet-signals build that predates this field the loaded page is
  // the honest fallback, because a span count of the page is still a span
  // count. The token total deliberately has no such fallback: a page-scoped
  // total is the thing this change exists to prevent, so an older build shows
  // a dash there instead.
  const spanCount = data?.span_count ?? data?.spans.length ?? 0
  const attribution = data?.attribution ?? null
  // A total summed over calls that did not all report usage speaks for fewer
  // calls than the trace made, and an operator comparing two traces has no way
  // to see that from the number alone. The count that carries this is
  // unmeasured_calls, not no_usage_calls: an emitter that loses its usage still
  // sends an object, which the latter accepts as a report.
  const unreported = data?.unmeasured_calls ?? 0
  // ADR-188 fixes this wording: `unreported: N of M calls`. The denominator is
  // the point — the qualifier matters most when the total is null, where an em
  // dash alone cannot tell a trace that made no calls from one that made
  // twenty-three and measured none of them.
  const tokenTotal = unreported > 0
    ? `${formatCount(data?.total_tokens)} · unreported: ${formatCount(unreported)} of ${formatCount(data?.calls)} calls`
    : formatCount(data?.total_tokens)

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">LLM Usage trace</h1>
          <p className="mt-1 break-all font-mono text-xs text-gray-500">{traceId || 'unknown trace'}</p>
        </div>
        <Link to={backHref} className="text-sm font-medium text-indigo-700 hover:text-indigo-900">← Back to traces</Link>
      </div>
      <TraceHealth health={data?.health} />
      {effectiveError && <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{effectiveError}</div>}
      {data?.telemetry === 'unavailable' && <TelemetryBanner />}
      {loading && <p className="text-sm text-gray-500">Loading trace…</p>}
      {data && data.telemetry !== 'unavailable' && !loading && (
        <>
          <section aria-label="Trace summary" className="grid grid-cols-2 gap-3 rounded-lg border border-gray-200 bg-white p-4 md:grid-cols-6">
            <HealthCard label="Spans" value={formatCount(spanCount)} />
            {/* The reader sums every usage row in the trace, so this stands
                while the span list is still paging. A total is null, and reads
                as an em dash, when nothing reported usage: zero is a
                measurement and silence is not. Tokens only, never a price:
                ADR-183. */}
            <HealthCard label="Tokens" value={tokenTotal} />
            <HealthCard label="Activity" value={attribution?.activity ?? '—'} />
            <HealthCard label="Cron" value={cronDisplayName(attribution) ?? '—'} />
            {/* 'complete' is a claim about capture, not about success; the
                per-span statuses in the tree carry the outcome. */}
            <HealthCard label="State" value={data.incomplete ? 'incomplete' : 'complete'} />
            <HealthCard label="Events" value={formatCount(data.delegation_events.length)} />
          </section>
          <SpanTree
            spans={data.spans}
            attribution={attribution}
            onLoadMore={data.next_cursor ? loadMore : undefined}
          />
          {loadingMore && <p className="text-sm text-gray-500">Loading more spans…</p>}
        </>
      )}
    </div>
  )
}

function ShareBar({ share }: { share: number }): React.ReactNode {
  const percentage = Math.max(0, Math.min(100, Math.round(share * 100)))
  return (
    <div className="flex items-center gap-2 min-w-28" aria-label={`Share ${percentage}%`}>
      <div className="h-2 bg-gray-100 rounded-full flex-1 overflow-hidden">
        <div
          className="h-full rounded-full bg-indigo-500"
          style={{ width: `${percentage}%` }}
        />
      </div>
      <span className="w-10 text-right text-xs text-gray-500">{percentage}%</span>
    </div>
  )
}

function HealthCard({ label, value, alert = false }: {
  label: string
  value: string
  alert?: boolean
}): React.ReactNode {
  return (
    <div
      role={alert ? 'alert' : undefined}
      className={alert
        ? 'rounded-lg border border-red-300 bg-red-50 px-4 py-3'
        : 'rounded-lg border border-gray-200 bg-white px-4 py-3'}
    >
      <p className={alert
        ? 'text-xs font-medium uppercase tracking-wide text-red-700'
        : 'text-xs font-medium uppercase tracking-wide text-gray-500'}
      >
        {label}: {value}
      </p>
    </div>
  )
}

function TokenCell({
  value,
  missing,
  calls,
}: {
  value: number | null
  missing?: number
  calls?: number
}): React.ReactNode {
  return (
    <div>
      <div className="font-mono text-sm">{formatCount(value)}</div>
      {missing != null && missing > 0 && calls != null && (
        <div className="text-xs text-gray-500">unreported: {missing} of {calls} calls</div>
      )}
    </div>
  )
}

function RollupRow({
  row,
  groupBy,
  filters,
  total = false,
}: {
  row: LlmUsageRollupRow
  groupBy: string
  filters?: Pick<LlmUsageQueryParams, 'window' | 'skill' | 'provider'>
  total?: boolean
}): React.ReactNode {
  const label = total ? 'Total' : rowLabel(row)
  const className = total
    ? 'border-t-2 border-gray-300 bg-gray-50 font-semibold'
    : row.key === 'unattributed'
      ? 'border-t border-gray-100 bg-gray-50 text-gray-500'
      : 'border-t border-gray-100'

  return (
    <tr className={className}>
      <td className="px-4 py-3 align-top">
        {total ? (
          <span>{label}</span>
        ) : (
          <Link
            to={traceHref(groupBy, row, filters)}
            className="font-medium text-indigo-700 hover:text-indigo-900"
          >
            {label}
          </Link>
        )}
        {row.trigger && !total && (
          <span className="ml-2 text-xs text-gray-400">{row.trigger}</span>
        )}
        {groupBy === 'activity' && row.key !== 'unattributed' && !total && row.includes_authoring === true && (
          <span className="ml-2 text-xs text-gray-400">includes authoring</span>
        )}
      </td>
      <td className="px-4 py-3 text-right align-top">{formatCount(row.calls)}</td>
      <td className="px-4 py-3 text-right align-top"><TokenCell value={row.input_tokens} missing={row.missing_buckets?.input_tokens} calls={row.calls} /></td>
      <td className="px-4 py-3 text-right align-top"><TokenCell value={row.cache_read_tokens} missing={row.missing_buckets?.cache_read_tokens} calls={row.calls} /></td>
      <td className="px-4 py-3 text-right align-top"><TokenCell value={row.cache_write_tokens} missing={row.missing_buckets?.cache_write_tokens} calls={row.calls} /></td>
      <td className="px-4 py-3 text-right align-top"><TokenCell value={row.output_tokens} missing={row.missing_buckets?.output_tokens} calls={row.calls} /></td>
      <td className="px-4 py-3 text-right align-top"><TokenCell value={row.reasoning_tokens} missing={row.missing_buckets?.reasoning_tokens} calls={row.calls} /></td>
      <td className="px-4 py-3 text-right align-top font-semibold">{formatCount(row.total_tokens)}</td>
      <td className="px-4 py-3 text-right align-top">{formatCount(row.unattributed_calls)}</td>
      <td className="px-4 py-3 text-right align-top">
        {row.no_usage_calls > 0 ? `No usage calls: ${formatCount(row.no_usage_calls)}` : '—'}
      </td>
      <td className="px-4 py-3 align-top">
        {row.substitutions.count > 0 ? (
          <div className="space-y-1 text-xs">
            <div>{formatCount(row.substitutions.count)} substitution{row.substitutions.count === 1 ? '' : 's'}</div>
            {!total && row.substitutions.pairs.map(pair => (
              <div key={`${pair.requested}-${pair.answered}`} className="whitespace-nowrap text-gray-600">
                {pair.requested} → {pair.answered} ({formatCount(pair.calls)})
              </div>
            ))}
          </div>
        ) : '—'}
      </td>
      <td className="px-4 py-3 text-right align-top">
        {row.p99_latency_ms == null ? '—' : `${formatCount(row.p99_latency_ms)}ms`}
      </td>
      <td className="px-4 py-3 align-top"><ShareBar share={row.share} /></td>
    </tr>
  )
}

function LlmUsageRollup() {
  const [searchParams, setSearchParams] = useSearchParams()
  const window = searchParams.get('window') ?? FILTER_DEFAULTS.window
  const groupBy = searchParams.get('group_by') ?? FILTER_DEFAULTS.group_by
  const skill = searchParams.get('skill') ?? FILTER_DEFAULTS.skill
  const provider = searchParams.get('provider') ?? FILTER_DEFAULTS.provider
  const [data, setData] = useState<LlmUsageRollupResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const loadRollup = useCallback(async () => {
    setLoading(true)
    setError(null)
    const params: LlmUsageQueryParams = {
      window,
      group_by: groupBy,
      skill: skill.trim() || undefined,
      provider: provider.trim() || undefined,
    }
    try {
      setData(await api.getLlmUsageRollup(params))
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Unable to load LLM Usage.')
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [groupBy, provider, skill, window])

  useEffect(() => {
    void loadRollup()
  }, [loadRollup])

  const setFilter = useCallback((key: keyof typeof FILTER_DEFAULTS, value: string) => {
    const next = new URLSearchParams(searchParams)
    const trimmed = value.trim()
    if (trimmed && trimmed !== FILTER_DEFAULTS[key]) next.set(key, trimmed)
    else next.delete(key)
    setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams])

  const health = data?.health
  const unavailable = data?.telemetry === 'unavailable'

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">LLM Usage</h1>
        <p className="mt-1 text-sm text-gray-500">Retained model usage and attribution for this fleet.</p>
      </div>

      {error && (
        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <TraceHealth health={health} />

      <section className="flex flex-wrap items-end gap-4 rounded-lg border border-gray-200 bg-white p-4" aria-label="LLM Usage filters">
        <label className="flex min-w-36 flex-col gap-1 text-xs font-medium text-gray-600">
          Window
          <select
            aria-label="Window"
            value={window}
            onChange={event => setFilter('window', event.target.value)}
            className="rounded-md border border-gray-300 bg-white px-2 py-2 text-sm font-normal text-gray-800"
          >
            {WINDOWS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
        </label>
        <label className="flex min-w-36 flex-col gap-1 text-xs font-medium text-gray-600">
          Group by
          <select
            aria-label="Group by"
            value={groupBy}
            onChange={event => setFilter('group_by', event.target.value)}
            className="rounded-md border border-gray-300 bg-white px-2 py-2 text-sm font-normal text-gray-800"
          >
            {GROUPS.map(group => <option key={group} value={group}>{group}</option>)}
          </select>
        </label>
        <label className="flex min-w-40 flex-col gap-1 text-xs font-medium text-gray-600">
          Skill
          <input
            aria-label="Skill"
            value={skill}
            onChange={event => setFilter('skill', event.target.value)}
            className="rounded-md border border-gray-300 px-2 py-2 text-sm font-normal text-gray-800"
            placeholder="All skills"
          />
        </label>
        <label className="flex min-w-40 flex-col gap-1 text-xs font-medium text-gray-600">
          Provider
          <input
            aria-label="Provider"
            value={provider}
            onChange={event => setFilter('provider', event.target.value)}
            className="rounded-md border border-gray-300 px-2 py-2 text-sm font-normal text-gray-800"
            placeholder="All providers"
          />
        </label>
      </section>

      {unavailable && <TelemetryBanner message="Usage data is temporarily unavailable; try again later." />}

      {!unavailable && data && !loading && (
        data.rows.length === 0
          ? <p className="rounded-lg border border-gray-200 bg-white px-5 py-10 text-center text-sm text-gray-500">No LLM usage recorded in the selected window.</p>
          : <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
              <table className="min-w-[1200px] w-full">
                <caption className="sr-only">LLM Usage rollup</caption>
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Group</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Calls</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Input</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Cache read</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Cache write</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Output</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Reasoning</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Total tokens</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Unattributed</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">No usage</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Substitutions</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">P99 latency</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Share</th>
                  </tr>
                </thead>
                <tbody>
                  <RollupRow row={data.totals} groupBy={groupBy} filters={{ window, skill, provider }} total />
                  {data.rows.map(row => <RollupRow key={`${row.key}-${row.label}`} row={row} groupBy={groupBy} filters={{ window, skill, provider }} />)}
                </tbody>
              </table>
            </div>
      )}

      {loading && !data && <p className="text-sm text-gray-500">Loading LLM Usage…</p>}
    </div>
  )
}

export function LlmUsage() {
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const traceListView = location.pathname.endsWith('/traces') || searchParams.get('view') === 'traces'
  const traceDetailView = location.pathname.endsWith('/trace') || searchParams.get('view') === 'trace'

  if (traceDetailView) return <LlmUsageTrace />
  if (traceListView) return <LlmUsageTraces />
  return <LlmUsageRollup />
}
